import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { BASE_PATH } from '../../api';
import { AuthService } from './auth.service';

export type EstadoAsociacion = 'activa' | 'desactivada' | 'sin-definir';

/** Estado legible a partir del campo heredado `active` (1 activa, 0 desactivada, NULL sin decidir). */
export function estadoDe(active: number | boolean | null | undefined): EstadoAsociacion {
  if (active === null || active === undefined) return 'sin-definir';
  return Number(active) === 1 ? 'activa' : 'desactivada';
}

export const ETIQUETA_ESTADO: Record<EstadoAsociacion, string> = {
  'activa': 'Activa',
  'desactivada': 'Desactivada',
  'sin-definir': 'Sin definir (dato heredado)'
};

// --- Operación en lote -------------------------------------------------------------------------------------------------

export interface ItemLote {
  id: number;
  nombre?: string | null;
  cif?: string | null;
  tipo?: number | null;
  anterior?: number | null;
  nuevo?: number;
  resultado: 'cambiara' | 'sin_cambios' | 'error' | 'aplicada';
  motivo?: string;
}

export interface PreviaLote {
  active: 0 | 1;
  items: ItemLote[];
  resumen: { seleccionadas: number; cambiaran: number; sinCambios: number; errores: number };
  huella: string;
  /** Frase que debe teclear la persona para confirmar (p. ej. «DESACTIVAR 3»). */
  confirmacion: string;
}

export interface ResultadoLote {
  operacion: string;
  active: 0 | 1;
  items: ItemLote[];
  resumen: { seleccionadas: number; aplicadas: number; sinCambios: number; errores: number };
}

export const LOTE_MAX = 100;

/**
 * Estado (activa / desactivada) de las asociaciones. Endpoints propios y auditados: PUT /asociaciones/{id}/estado y
 * POST /asociaciones/estado/lote. Solo muestra u oculta opciones en la interfaz; la API exige el permiso global
 * `asociaciones:estado` en cada petición.
 */
@Injectable({ providedIn: 'root' })
export class AsociacionEstadoService {
  constructor(
    private readonly http: HttpClient,
    private readonly auth: AuthService,
    @Inject(BASE_PATH) private readonly basePath: string
  ) {}

  private headers(): HttpHeaders {
    return new HttpHeaders({ Authorization: 'Bearer ' + this.auth.getToken() });
  }

  /** Devuelve el valor `active` que ha quedado guardado (1 o 0). */
  cambiar(asociacionId: number, activa: boolean, motivo?: string): Observable<number | null> {
    const body: { active: 0 | 1; motivo?: string } = { active: activa ? 1 : 0 };
    if (motivo && motivo.trim()) body.motivo = motivo.trim().slice(0, 255);
    return this.http
      .put<{ asociaciones?: { active?: number | null }[] }>(`${this.basePath}/asociaciones/${encodeURIComponent(String(asociacionId))}/estado`, body, { headers: this.headers() })
      .pipe(map(res => res.asociaciones?.[0]?.active ?? (activa ? 1 : 0)));
  }

  /** Vista previa (no escribe): resultado por asociación, recuentos exactos, huella y frase de confirmación. */
  previsualizarLote(ids: number[], activa: boolean): Observable<PreviaLote> {
    return this.http
      .post<{ lote: PreviaLote }>(`${this.basePath}/asociaciones/estado/lote`, { active: activa ? 1 : 0, ids, modo: 'previa' }, { headers: this.headers() })
      .pipe(map(res => res.lote));
  }

  /** Ejecuta la operación previsualizada: exige la huella de la vista previa y la frase de confirmación. */
  ejecutarLote(ids: number[], activa: boolean, huella: string, confirmacion: string, motivo?: string): Observable<ResultadoLote> {
    const body: Record<string, unknown> = { active: activa ? 1 : 0, ids, modo: 'ejecutar', huella, confirmacion };
    if (motivo && motivo.trim()) body['motivo'] = motivo.trim().slice(0, 255);
    return this.http
      .post<{ lote: ResultadoLote }>(`${this.basePath}/asociaciones/estado/lote`, body, { headers: this.headers() })
      .pipe(map(res => res.lote));
  }
}
